/**
 * WIT-FIX-RETEST-1008-001 — the welder's side of «Ferdig sveist» (P-B / P-C /
 * P-D), as pure decisions over the server's certificate-autoselect answer so
 * node --test can pin them (tests/js/cert-sign.test.js). The server remains
 * the boundary: it filters the candidates, computes the lock and refuses the
 * sign-off again on register-weld; this only decides what the screen does
 * with the answer it got.
 */

/**
 * What a WELDER's sign-off does with the autoselect answer:
 *   proceed  matching off / unavailable / nothing usable → post; the server
 *            judges («Varsle» signs flagged, «Blokker» refuses with its text)
 *   blocked  the server's padlock: tell, never post, never ask
 *   auto     one certificate covers the weld: use it silently
 *   confirm  nothing verifies, candidates the server still offers: ask which
 *            one was welded to (figure 1), marked «Bekreftet manuelt»
 */
export function welderCertDecision (data) {
  if (!data || !data.enabled) return { action: 'proceed', ids: [], candidates: [], message: '' }
  const ids = (data.ids || []).map(String)
  const candidates = (data.candidates || []).map(String)
  if (data.locked) return { action: 'blocked', ids: [], candidates: [], message: String(data.lock_message || data.message || '') }
  if (data.result === 'auto' && ids.length) return { action: 'auto', ids, candidates: [], message: '' }
  if (candidates.length) return { action: 'confirm', ids, candidates, message: String(data.message || '') }
  return { action: 'proceed', ids, candidates: [], message: String(data.message || '') }
}

/**
 * WIT-FIX-CERT-5355-001 (Erik 15.10, section 3): nothing the welder holds
 * covers the weld (no auto pick, nothing to confirm, no padlock) and the
 * policy is not «Av» → the short reason dialog before anything is posted.
 * The server refuses such a sign-off without a reason (D-5355-1), so this
 * is the one question the welder gets.
 */
export function welderReasonNeeded (data, decision) {
  if (!data || !data.enabled) return false
  if ((data.enforcement || 'warn') === 'off') return false
  return !!decision && decision.action === 'proceed'
}

/**
 * The confirm dialog's options from the server's candidates: label, the
 * server's one-line reason and the verdict. A candidate the server judged
 * not_qualified is never offered (P-B), whatever an older server sent.
 */
export function confirmOptions (candidates, verdicts, labelOf) {
  const v = verdicts || {}
  return (candidates || []).map(String)
    .filter(id => !v[id] || v[id].verdict !== 'not_qualified')
    .map(id => ({
      value: id,
      label: (labelOf && labelOf(id)) || id,
      why: (v[id] && v[id].why) || '',
      verdict: (v[id] && v[id].verdict) || 'cannot_verify'
    }))
}

/** P-C: the «Bekreftet manuelt» line is shown only when a cannot_verify choice is on offer. */
export function needsManualNote (options) {
  return (options || []).some(o => o && o.verdict === 'cannot_verify')
}

/**
 * WIT-FIX-CERT-5348-001 F1 / D-CERT-5 — the office's answer to the reason
 * dialog («Ikke dekket av sertifikat»):
 *   AVBRYT (null)   never signs, in any mode — the weld stays Kartlagt
 *   SIGNER + text   an override: cert_selection 'override' with the reason
 *   SIGNER, empty   (warn only — block requires text) a plain sign-off the
 *                   server stores not_qualified and flags; not an override
 */
export function officeReasonOutcome (reason) {
  if (reason === null || reason === undefined) return { proceed: false, reason: '', selection: null }
  const text = String(reason).trim()
  return text ? { proceed: true, reason: text, selection: 'override' } : { proceed: true, reason: '', selection: null }
}

/**
 * F5a — which yellow-box wording the sheet shows for an advisory weld:
 *   none           no certificate at all («Du har ingen sveisesertifikat…»)
 *                  or none valid
 *   not_qualified  certificates held, none qualifies; codes = the processes
 *                  none of them covers (server cert_uncovered_processes)
 * An older server sends no kind: the advisory only fires when nothing is
 * usable, so it is read as not_qualified (never as cannot_verify).
 */
export function advisoryCopy ({ kind, uncovered, ownState, reason }) {
  const codes = (Array.isArray(uncovered) ? uncovered : []).map(String).filter(Boolean).join(', ')
  if (ownState === 'no_certificates') return { key: 'no_certificates', codes: '' }
  // WIT-FIX-CERT-5355-001 F5a: the server's decisive line wins when it sends one.
  const line = String(reason || '').trim()
  if (line && kind !== 'none') return { key: 'reason', codes, reason: line }
  if (kind === 'none') return { key: 'none_valid', codes: '' }
  return { key: 'not_qualified', codes }
}

/**
 * F5b / D-CERT-9 — the welder's OWN certificates under the yellow box, as the
 * server ranked them: «certificate · verdict · first failing dimension».
 * Read-only; nothing here can add a certificate the server did not send.
 */
export function advisoryRankedRows (ranked, verdictText) {
  const vt = verdictText || {}
  return (Array.isArray(ranked) ? ranked : []).map(r => {
    const ff = r && r.first_failing
    return {
      id: String(r.id),
      label: String(r.label || r.id),
      verdict: r.verdict || '',
      verdictText: vt[r.verdict] || r.verdict || '',
      failing: ff ? String(ff.label || ff.dimension || '') : '',
      detail: ff ? String(ff.detail || '') : ''
    }
  })
}

/**
 * F5a — one sentence in the reason dialog: a ranked row whose reason the
 * server's message already says is not repeated under it.
 */
export function dedupeRankedWhy (message, options) {
  const norm = s => String(s || '').toLowerCase().replace(/[.\s]+$/u, '').trim()
  const m = norm(message)
  return (options || []).map(o => (o && o.why && m && m.includes(norm(o.why))) ? Object.assign({}, o, { why: '' }) : o)
}

/**
 * WIT-FIX-CERT-5355-001: a key that has no translation reads as words, never
 * as «MESSAGE.WELDMAPPING.AUDITACTION.FLAG» — the last path segment,
 * snake/camel case split, first letter capitalised.
 */
export function humaniseKey (key) {
  const last = String(key || '').split('.').pop() || ''
  const words = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : String(key || '')
}
